import type { TourNotification } from '@entities/notification';

export type Size = 'sm' | 'lg';

export enum CredentialProvidersEnum {
  AZURE_AD = 'azure-ad',
}

export enum ProxyActionsEnum {
  PROXY_IN = 'proxy-in',
  PROXY_OUT = 'proxy-out',
}

export type RouteObject = {
  key: string;
  title: string;
  icon: JSX.Element;
  path: string;
  routeComponents?: RouteObject[];
};

type MonthData = {
  month: string;
  year: string;
  value: number;
};

export interface Headcount {
  filledPositions: number;
  directFilledPositions: number;

  vacantPositions: {
    totalVacant: number;
    directTotalVacant: number;
    filledPositions: number;
    directFilletPositins: number;
  };

  lastMonths: MonthData[];
  directLastMonths: MonthData[];

  vacantLastMonths: MonthData[];
  directVacantLastMonths: MonthData[];

  byMonth: Record<string, number>;
  directByMonth: Record<string, number>;

  perCountry: {
    countryCode: string;
    amount: number;
  }[];

  directPerCountry: {
    countryCode: string;
    amount: number;
  }[];

  minTimeInCompany: number;
  averageTimeInCompany: number;
  maxTimeInCompany: number;
  directMinTimeInCompany: number;
  directAverageTimeInCompany: number;
  directMaxTimeInCompany: number;

  minTimeInPosition: number;
  averageTimeInPosition: number;
  maxTimeInPosition: number;
  directMinTimeInPosition: number;
  directAverageTimeInPosition: number;
  directMaxTimeInPosition: number;

  minTimeInBand: number;
  averageTimeInBand: number;
  maxTimeInBand: number;
  directMinTimeInBand: number;
  directAverageTimeInBand: number;
  directMaxTimeInBand: number;

  top30TimeInPositionDays: {
    employeeGlobalId: string;
    time: number;
    employee: {
      firstName: string;
    };
  }[];

  directTop30TimeInPositionDays: {
    employeeGlobalId: string;
    time: number;
    employee: {
      firstName: string;
    };
  }[];

  top30TimeInBandDays: {
    employeeGlobalId: string;
    time: number;
    employee: {
      firstName: string;
    };
  }[];

  directTop30TimeInBandDays: {
    employeeGlobalId: string;
    time: number;
    employee: {
      firstName: string;
    };
  }[];

  top30TimeInCompanyDays: {
    employeeGlobalId: string;
    time: number;
    employee: {
      firstName: string;
    };
  }[];

  directTop30TimeInCompanyDays: {
    employeeGlobalId: string;
    time: number;
    employee: {
      firstName: string;
    };
  }[];
  cacheDate: string;
}

export interface Termination {
  totalYearToDate: number;
  directTotalYearToDate: number;
  byMonth: number[];
  turnoverYearToDate: number;
  turnoverEvolutionByMonth: number[];
  directTurnoverYearToDate: number;
  directTurnoverEvolutionByMonth: number[];
  peerGroupName: string;
  totalVoluntary: number;
  directTotalVoluntary: number;
  totalInvoluntary: number;
  directTotalInvoluntary: number;
  peerGroup: {
    peerGroupDirectTurnoverEvolutionByMonth: number[];
    peerGroupTurnoverEvolutionByMonth: number[];
    peerGroupTurnoverYearToDate: number;
    peerGroupDirectTurnoverYearToDate: number;
    __typename: string;
  };
  abi: {
    turnoverYearToDate: number;
    turnoverEvolutionByMonth: number[];
    __typename: string;
  };
  __typename: string;
  cacheDate: string;
}

type Score = {
  score: number;
  name: string;
};

type EngagementData = {
  completionRate?: number;
  size?: number;
  completionRateAnnual?: number;
  completionRateTouchpoint?: number;
  directCompletionRateAnnual?: number;
  directCompletionRateTouchpoint?: number;
  sizeAnnual?: number;
  directSizeAnnual?: number;
  sizeTouchpoint?: number;
  directSizeTouchpoint?: number;
  totalOverallResultsAnnual?: number;
  totalManagerEffectivenessAnnual?: number;
  directOverallResultsAnnual?: number;
  directManagerEffectivenessAnnual?: number;
  totalOverallResultsTouchpoint?: number;
  totalManagerEffectivenessTouchpoint?: number;
  directOverallResultsTouchpoint?: number;
  directManagerEffectivenessTouchpoint?: number;
  topScoreAnnual?: Score[];
  directTopScoreAnnual?: Score[];
  bottomScoreAnnual?: Score[];
  directBottomScoreAnnual?: Score[];
  topScoreTouchpoint?: Score[];
  directTopScoreTouchpoint?: Score[];
  bottomScoreTouchpoint?: Score[];
  directBottomScoreTouchpoint?: Score[];
};

type Engagement = {
  currentYear: EngagementData;
  lastYear: EngagementData;
};

type EngagementABI = {
  abiOverallResultsCurrentYearAnnual: number;
  abiManagerEffectivenessCurrentYearAnnual: number;
  abiOverallResultsLastYearAnnual: number;
  abiManagerEffectivenessLastYearAnnual: number;
  abiOverallResultsCurrentYearTouchpoint: number;
  abiManagerEffectivenessCurrentYearTouchpoint: number;
  abiOverallResultsLastYearTouchpoint: number;
  abiManagerEffectivenessLastYearTouchpoint: number;
};

type EngagementPeerGroup = {
  overallResultsCurrentYear: number;
  managerEffectivenessCurrentYear: number;
  directOverallResultsCurrentYear: number;
  directManagerEffectivenessCurrentYear: number;
  overallResultsLastYear: number;
  managerEffectivenessLastYear: number;
  directOverallResultsLastYear: number;
  directManagerEffectivenessLastYear: number;
};

type LCMAverageCapability = {
  name: string;
  score: number;
};

type LCMResults = {
  average180: number;
  average360: number;
  directAverage180: number;
  directAverage360: number;
  averageCapabilities: LCMAverageCapability[];
  directAverageCapabilities: LCMAverageCapability[];
  cacheDate: string;
};

export type Children = {
  firstName: string;
  lastName: string;
  employeeGlobalId: string;
  supervisoryOrg: string;
  employeeBand: string;
  positionTitle: string;
  zone: string;
  managerName: string;
  countryCode: string;
  sltLevel: string;
  email: string;
  expatStatus: string;
  partnerStatus: string;
  timeInBandYears: string;
  timeInBandMonths: string;
  timeInPositionYears: string;
  timeInPositionMonths: string;
  timeInCompanyYears: string;
  timeInCompanyMonths: string;
  isDirectEmployee: boolean;
  birthday: string;
  workAnniversary: string;
  annualSalary: string;
  engagement: string;
  timeoffBalance: string;
  avgLcmScore: string;
  isCurrentlyOverlapped?: boolean;
  overlapEmployeeGlobalId?: string | null;
  overlapEmployee?: Record<string, any> | null;
};

export type TerminationForList = {
  employeeGlobalId: string;
  firstName: string;
  lastName: string;
  positionTitle: string;
  managerGlobalName: string;
  zone: string;
  countryCode: string;
  employeeBand: string;
  sltLevel: string;
  expatStatus: string;
  partnerStatus: string;
  timeInBandDays: string;
  timeWithCompanyDays: string;
  terminationDate: string;
  terminationType: string;
  terminationReason: string;
  turnoverFlag: string;
  isDirectEmployee: boolean;
};

export type FullTeamInformationTermination = {
  nextCursor: string;
  terminations: TerminationForList[];
};

export type FullTeamEmployeeData = {
  cursor: string;
  employees: Children[];
};

export type NewEngagement = {
  type: 'Annual' | 'Touchpoint';
  year: string;
  totalOverallResults: number | null;
  totalManagerEffectiveness: number | null;
  directOverallResults: number | null;
  directManagerEffectiveness: number | null;
  size: number;
  directSize: number;
  completionRate: number;
  directCompletionRate: number;
  topScore: Score;
  directTopScore: Score;
  bottomScore: Score;
  directBottomScore: Score;
  cacheDate: string;
};

export type NewEngagementABI = {
  type: string;
  year: string;
  managerEffectiveness: number;
  overallResults: number;
};

type EmployeeDataCompensation = {
  firstName: string;
  lastName: string;
};

export type Compensation = {
  currency: string;
  payValue: number;
  employee: EmployeeDataCompensation;
};

export type BandDTO = {
  band: string;
  compensation: Compensation[];
};

export type CountryData = {
  workCountry: string;
  currencies: string[];
  dto: BandDTO[];
};

export type DataStructure = {
  direct: CountryData[];
  all: CountryData[];
};

export interface Vacation {
  employee: TimeoffEmployee;
  startDate: Date;
  endDate: Date;
}

export interface ITimeoff {
  currently: Vacation[];
  coming: Vacation[];
  pending: Vacation[];
  directCurrently: Vacation[];
  directComing: Vacation[];
  directPending: Vacation[];
}

export interface Timeoff {
  startDate: string;
  endDate: string;
  employee: TimeoffEmployee;
}

export interface TimeoffByMonth {
  month: string;
  timeoffs: Timeoff[];
}

type TimeoffEmployee = {
  employeeGlobalId?: string;
  firstName: string;
  lastName: string;
};

export interface TimeoffData {
  startDate: string;
  endDate: string;
  employee: TimeoffEmployee;
}

export interface ITimeoffData {
  cacheDate: string;
  byMonth: TimeoffByMonth[];
  directByMonth: TimeoffByMonth[];
  next30Days: TimeoffData[];
  directNext30Days: TimeoffData[];
  pendentTimeoffs: TimeoffData[];
  directPendentTimeoffs: TimeoffData[];
}

export interface Balance {
  employeeGlobalId: string;
  totalDaysAvailable: number;
  totalHoursAvailable: number;
  employee: Pick<Employee, 'firstName' | 'lastName'>;
}

interface BalanceData {
  balanceList: Balance[];
  directBalanceList: Balance[];
}

type EmployeeTimeoffBalance = {
  absencePlan: string;
  unit: 'Days' | 'Hours' | string;
  quantity: number;
};

export type EmployeeBalanceTimeoff = {
  employeeGlobalId: string;
  employee: Pick<Employee, 'firstName' | 'lastName'>;
  totalDaysAvailable: number;
  totalHoursAvailable: number;
  employeeTimeoffBalance: EmployeeTimeoffBalance[];
};

export interface EmployeeData {
  employeeGlobalId: string;
  firstName: string;
  lastName: string;
  headcount: Headcount;
  compensation: DataStructure;
  termination: Termination;
  insights?: Insight[];
  engagement: Engagement;
  lcmResults: LCMResults;
  engagementABI: EngagementABI;
  engagementPeerGroup: EngagementPeerGroup;
  movements: Movements;
  children: Children[];
  target: Target;
  newEngagement: NewEngagement[];
  newEngagementABI: NewEngagementABI[];
  timeoff: ITimeoffData;
  timeoffBalance: BalanceData;
  terminationDistribution: TerminationDistribution;
}

export interface Employee {
  timeoffBalance?: number;
  preferredFirstName?: string;
  preferredLastName?: string;
  zone?: string;
  employeeGlobalId?: string;
  employeeId?: string;
  id?: string;
  managerUid?: string;
  firstName?: string;
  lastName?: string;
  managerGlobalId?: string;
  supervisoryOrg?: string;
  employeeBand?: string;
  employeeBandGroup?: string;
  childrenIds?: string[];
  email?: string;
  children?: string[];
  totalChildrenCount?: number;
  managerHierarchy?: string;
  globalBand?: string;
  sltLevel?: string;
  function?: string;
  headcount?: Headcount;
  termination?: Termination;
  timeInPositionYear?: string;
  countryCode?: string;
  workLocationName?: string;
  engagement?: Engagement;
  lcmResults?: LcmResults;
  engagementABI?: EngagementABI;
  positionTitle?: string;
  manager?: {
    id: string;
    firstName: string;
    lastName: string;
    employeeGlobalId: string;
    function: string;
    countryCode: string;
    positionTitle: string;
    positionId: string;
  };
}

export type GetEmployeesFilterParams = {
  employeeNameOrId?: string;
  country?: string;
  supOrg?: string;
};

export type User = {
  globalId: string | null;
  employeeId: string;
  name: string;
  band: string | null;
  email: string;
  proxiedAs: User | null;
  refreshToken: string;
  token: string;
  roles: string[];
  zone: string | null;
};

export interface Insight {
  section: string;
  label: string;
  payload?: string;
  outcome?: 'above' | 'below';
  value?: number;
}

export interface LcmResults {
  average180?: number;
  average360?: number;
}

export type Movement = {
  supOrgId: string;
  businessProcess: string;
  firstName: string;
  lastName: string;
  newPositionName: string;
  lastPositionName: string;
  newManagerId: string;
  lastManagerId: string;
  employeeGlobalId: string;
  localEmployeeId: string;
  previousPositionId: string;
  newPositionId: string;
  bandMovementCheck: string;
  businessProcessReason: string;
  businessProcessSubreason: string;
  overallBusinessProcess: string;
  email: string;
  nextSupOrgId: string;
  loadDate: string;
  newManager: Employee;
};

export type Movements = {
  leavingCount: number;
  directLeavingCount: number;
  promotionCount: number;
  directPromotionCount: number;
  demotionCount: number;
  directDemotionCount: number;
  lateralCount: number;
  directLateralCount: number;
  hireCount: number;
  directHireCount: number;
  movements: [Movement];
  directMovements: [Movement];
  cacheDate: string;
};

export type TargetList = {
  employeeGlobalId: string;
  employeeAchievement: number;
  firstName: string;
  lastName: string;
  isActive: boolean;
};

export type Target = {
  year: number;
  total: number;
  directTotal: number;
  completion: number;
  directCompletion: number;
  zoneAvg: number;
  teamAverage: number;
  directTeamAverage: number;
  list: [TargetList];
  directList: [TargetList];
  cacheDate: string;
};

export type Notification = {
  id: string;
  key: string;
  type: NotificationType;
  subtype: NotificationSubType;
  title: string;
  message: string;
  metadata: Record<string, unknown>;
  createdAt: Date;
  expiresAt: Date;
  chainId?: string;
  order?: number;
};

export type NotificationDTO = {
  key: string;
  type: NotificationType;
  subtype: NotificationSubType;
  title?: string;
  message?: string;
  amount: number;
  unit: NotificationTimeUnit;
  metadata?: Record<string, unknown>;
  chainId?: string;
  order?: number;
};

export type NotificationCacheData = {
  lastUpdated: string;
  notifications: Notification[];
};

export type NotificationGroup = {
  chainId: string;
  items: Notification[];
};

export type GroupedNotificationCacheData = {
  lastUpdated: Date | null;
  notifications: NotificationGroup[];
};

export type NotificationCleanupResult = {
  notificationsExpired: number;
  totalRemaining: number;
  status: string;
  message: string;
};

export enum NotificationType {
  SNACKBAR = 'SNACKBAR',
  POPUP = 'POPUP',
  HIGHLIGHT = 'HIGHLIGHT',
}

export enum NotificationSubType {
  WARNING = 'WARNING',
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS',
  PULSE = 'PULSE',
}

export enum NotificationTimeUnit {
  'HOURS' = 'HOURS',
  'DAYS' = 'DAYS',
  'WEEKS' = 'WEEKS',
}

export { RequestStatus } from '~/fsd/shared/types/app';
export { TaskStatus } from '~/fsd/shared/types/app';
export { FeatureNameEnum } from '~/fsd/shared/types/app';

export type NotificationBooleanResponse = {
  success: boolean;
  errors: string[];
};

type DistributionDetailItem = {
  label: string;
  value: number;
  percentage: number;
};

type DistributionItem = {
  label: string;
  value: number;
  percentage: number;
  details: DistributionDetailItem[];
};

type TerminationDistribution = {
  byReason: DistributionItem[];
  byCountry: DistributionItem[];
  byBand: DistributionItem[];
  byTimeInCompany: DistributionItem[];
  directByReason: DistributionItem[];
  directByCountry: DistributionItem[];
  directByBand: DistributionItem[];
  directByTimeInCompany: DistributionItem[];
};

export type EmployeePreferences = {
  firstName?: string;
  lastName?: string;
  preferences: QueryEmployeePreferencesData;
};

export type BaseEmployeePreferencesData = {
  id: string;
  managerGlobalId: string;
  preferredLanguage: string;
  reportType: string;
  lastAccessDate: string;
  tourCompleted: boolean;
};

export type EmployeePreferencesWidget = {
  name: string;
  size: Size;
  hide: boolean;
};

export interface QueryEmployeePreferencesData
  extends BaseEmployeePreferencesData {
  widgets: EmployeePreferencesWidget[];
}

export interface MutationEmployeePreferencesData
  extends BaseEmployeePreferencesData {
  widgets: string;
}

export type UserPreferencesInput = Omit<
  MutationEmployeePreferencesData,
  'id' | 'managerGlobalId'
>;

export type UpdateUserPreferencesInput = Pick<
  MutationEmployeePreferencesData,
  'id'
> &
  Partial<Omit<MutationEmployeePreferencesData, 'id'>>;

export type UpdateUserPreferencesWidgetData = Pick<
  EmployeePreferencesWidget,
  'name'
> &
  Partial<Omit<EmployeePreferencesWidget, 'name'>>;

export interface TutorialStep {
  targetId: string;
  tooltip: {
    id: string;
    title: string;
    content: string;
    type?: TourNotification['type'];
    subType?: TourNotification['subtype'];
    metadata?: Record<string, unknown>;
  };
  createdAt?: Date;
  order?: number;
}

export enum BringEmployeeStepsEnum {
  MANAGER_FORM = 'manager-form',
  EMPLOYEE_CURRENT_MANAGER_APPROVAL = 'employee-current-manager-approval',
  COMPLETE_REQUEST_DETAILS = 'complete-request-details',
  OFFER_LETTER = 'offer-letter',
  CONTRACT = 'contract',
  WORKDAY_PROCESSING = 'workday-processing',
  ZONE_REWARDS_MANAGER_APPROVAL = 'rewards-manager-approval',
  ZONE_REWARDS_DIRECTOR_APPROVAL = 'rewards-director-approval',
}
